/**
 * A definition's change lifecycle: the stepper (Drafted → MR !N open →
 * Merged = enabled) and the table of what that change stands on: the
 * definition's own lint, the policy check (the dispatch ceilings, as-is),
 * and, with an MR, the MR itself, its pipeline and its approvals.
 *
 * An enabled definition no MR changes has every step done; with an MR (a
 * proposed one, or a pending edit or removal) the current step is MR open.
 */

import type { ReactElement, ReactNode } from 'react';
import { splitFindings } from '../utils/policyFindings';
import type {
  DefinitionDetail,
  LintFinding,
  MrDetail,
} from '../types/projectPanel.types';

type StepState = 'done' | 'current' | 'todo';

function Stepper({ detail }: { detail: DefinitionDetail }): ReactElement {
  const pending = detail.summary.pending_mr;
  const iid = detail.mr?.iid ?? pending?.iid;
  const open = detail.state === 'proposed' || iid !== undefined;
  const steps: [string, StepState][] = [
    ['Drafted', 'done'],
    [iid === undefined ? 'MR open' : `MR !${String(iid)} open`, open ? 'current' : 'done'],
    [pending?.change === 'deleted' ? 'Merged = removed' : 'Merged = enabled', open ? 'todo' : 'done'],
  ];
  return (
    <ol className="def-stepper" aria-label="Change lifecycle">
      {steps.map(([label, state]) => (
        <li
          key={label}
          className={`def-step def-step--${state}`}
          {...(state === 'current' ? { 'aria-current': 'step' as const } : {})}
        >
          <span className="def-step__box">{label}</span>
        </li>
      ))}
    </ol>
  );
}

function FindingList({ findings }: { findings: LintFinding[] }): ReactElement {
  return (
    <ul className="def-findings">
      {findings.map((f) => (
        <li key={`${f.severity}:${f.path}:${f.message}`} className={`def-findings__item def-findings__item--${f.severity}`}>
          {f.severity === 'error' ? '✗' : '⚠'} {f.message}
        </li>
      ))}
    </ul>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <tr>
      <th scope="row">{label}</th>
      <td>{children}</td>
    </tr>
  );
}

function approvals(mr: MrDetail): string {
  if (mr.approvals_given === null || mr.approvals_required === null) return '—';
  return `${String(mr.approvals_given)} / ${String(mr.approvals_required)}`;
}

function policyCell(lint: LintFinding[], policy: LintFinding[]): ReactNode {
  if (policy.length > 0) return <FindingList findings={policy} />;
  // A definition that doesn't load never reaches the ceilings.
  if (lint.some((f) => f.severity === 'error')) return 'not checked (the definition has errors)';
  return <span className="def-ok">✓ within the project&apos;s ceilings</span>;
}

function MrRow({ mr }: { mr: MrDetail }): ReactElement {
  return (
    <Row label="MR">
      <a href={mr.url} target="_blank" rel="noreferrer" className="runs-page__link">
        !{mr.iid}
      </a>{' '}
      “{mr.title}” · {mr.author}
    </Row>
  );
}

export function DefinitionLifecycle({ detail }: { detail: DefinitionDetail }): ReactElement {
  const { lint, policy } = splitFindings(detail.summary.findings);
  const { mr } = detail;
  return (
    <div className="def-lifecycle">
      <Stepper detail={detail} />
      <table className="def-table">
        <tbody>
          {mr !== null && <MrRow mr={mr} />}
          <Row label="Definition lint">
            {lint.length > 0 ? <FindingList findings={lint} /> : <span className="def-ok">✓ valid</span>}
          </Row>
          <Row label="Policy check">{policyCell(lint, policy)}</Row>
          {mr !== null && <Row label="MR pipeline">{mr.pipeline_status ?? 'no pipeline'}</Row>}
          {mr !== null && <Row label="Approvals">{approvals(mr)}</Row>}
        </tbody>
      </table>
    </div>
  );
}
